import { Base64 } from 'js-base64'

/**
 * Utility function to assemble a v2 or v3 compliant Character Card PNG
 * 
 * @param {*} arrayBuffer PNG read as an array buffer
 * @param {Object | Object[]} dataJson JSON containing the data to encode and insert into the PNG. Should be in the format of {"keyword": keyword, "data": JSON} or as a list of objects in the aforementioned format to encode multiple specs.
 * @returns Promise that resolves to an array buffer containing the newly assembled PNG
 */
export default async function assembleNewPng (arrayBuffer, dataJson) {
    return new Promise((resolve, reject) => {
        const data = new DataView(arrayBuffer);
        const newChunks = [];

        let offset = 8; // Skipping the PNG header
        let textInserted = false;

        newChunks.push(new Uint8Array(arrayBuffer.slice(0, offset))); // Copying the original header

        const listOfChunks = Array.isArray(dataJson) ? dataJson : [dataJson];

        // Copying the rest of the PNG, inserting the tEXt chunks once, just before IEND. Image data
        // is usually split across many consecutive IDAT chunks, which must stay contiguous.
        while (offset < data.byteLength) {
            const length = data.getUint32(offset);
            const type = String.fromCharCode(
                data.getUint8(offset + 4),
                data.getUint8(offset + 5),
                data.getUint8(offset + 6),
                data.getUint8(offset + 7)
            );
            if (type === "IEND") {
                listOfChunks.forEach((item) => newChunks.push(buildTextChunk(item.keyword, item.data)));
                textInserted = true;
            }
            newChunks.push(new Uint8Array(arrayBuffer.slice(offset, offset + 8 + length + 4)));
            offset += 8 + length + 4
        }
        // A truncated PNG with no IEND still displays in browsers; don't silently export it without card data.
        if (!textInserted) {
            listOfChunks.forEach((item) => newChunks.push(buildTextChunk(item.keyword, item.data)));
        }

        const combinedLength = newChunks.reduce((acc, chunk) => acc + chunk.length, 0);
        const combinedArrayBuffer = new Uint8Array(combinedLength);
        let combinedOffset = 0;

        for (const chunk of newChunks) {
            combinedArrayBuffer.set(chunk, combinedOffset);
            combinedOffset += chunk.length;
        }

        resolve(combinedArrayBuffer.buffer);
    });    
}

function buildTextChunk(keyword, json) {
    const textChunkData = new TextEncoder().encode(`${keyword}\0${Base64.encode(JSON.stringify(json))}`);
    const textChunkLength = textChunkData.length;

    const textChunk = new Uint8Array(8 + textChunkLength + 4);
    const view = new DataView(textChunk.buffer);

    view.setUint32(0, textChunkLength);
    textChunk[4] = 't'.charCodeAt(0);
    textChunk[5] = 'E'.charCodeAt(0);
    textChunk[6] = 'X'.charCodeAt(0);
    textChunk[7] = 't'.charCodeAt(0);
    textChunk.set(textChunkData, 8);
    view.setUint32(8 + textChunkLength, crc32(textChunk.subarray(4, 8 + textChunkLength)));

    return textChunk;
}

/**
 * Utility function to calculate CRC32. Mostly magic to me if I'm being honest
 * 
 * @param {*} data 
 * @returns uhhhh the CRC apparently
 */
function crc32(data) {
    let crc = 0xffffffff;
    for (let i = 0; i < data.length; i++){
        crc = (crc >>> 8) ^ crcTable[(crc ^ data[i]) & 0xff];
    }
    return (crc ^ 0xffffffff) >>> 0;
}

// Precomputes CRC table, also frankly magic to me
const crcTable = (() => {
    let c;
    const table = new Array(256);
    for (let n = 0; n < 256; n++) {
        c = n;
        for (let k = 0; k < 8; k++) {
            c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1;
        }
        table[n] = c;
    }
    return table;
})();